
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Shield } from 'lucide-react'
import { toast } from 'sonner'

interface LicenseFormData {
  tenantId: string
  expiresAt: string
  maxUsers: number
}

interface GenerateLicenseModalProps {
  isOpen: boolean
  onClose: () => void
  extensionId: string | null
}

export function GenerateLicenseModal({ isOpen, onClose, extensionId }: GenerateLicenseModalProps) {
  const [licenseData, setLicenseData] = useState<LicenseFormData>({
    tenantId: '',
    expiresAt: '',
    maxUsers: -1,
  })

  const handleSubmit = async () => {
    if (!extensionId) return

    // This would call the actual API to generate license
    toast.success('Extension license generated', {
      description: 'License has been created for this extension.',
    })

    onClose()
    setLicenseData({
      tenantId: '',
      expiresAt: '',
      maxUsers: -1,
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Generate Extension License</CardTitle>
          <CardDescription>
            Create a license for this extension
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Tenant ID (Optional)</Label>
            <Input
              type="number"
              value={licenseData.tenantId}
              onChange={(e) => setLicenseData({ ...licenseData, tenantId: e.target.value })}
              placeholder="Leave empty for marketplace purchase"
            />
          </div>
          <div className="space-y-2">
            <Label>Expiration Date (Optional)</Label>
            <Input
              type="date"
              value={licenseData.expiresAt ? new Date(licenseData.expiresAt).toISOString().split('T')[0] : ''}
              onChange={(e) => setLicenseData({ ...licenseData, expiresAt: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">Leave empty for lifetime license</p>
          </div>
          <div className="space-y-2">
            <Label>Max Users (-1 for unlimited)</Label>
            <Input
              type="number"
              value={licenseData.maxUsers}
              onChange={(e) => setLicenseData({ ...licenseData, maxUsers: parseInt(e.target.value) || -1 })}
            />
          </div>
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSubmit} className="gap-2">
              <Shield className="h-4 w-4" />
              Generate License
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
